import { useState, useEffect } from 'react';
import { LogOut, Plus, BarChart3, AlertTriangle, Users, TrendingUp, Search, Clock, Activity, Pill, Sparkles, ShieldAlert } from 'lucide-react';
import { getAllPatients, getSessionsForPatient, addPatient, updatePatient, deletePatient } from '../lib/mock';
import { RISK_LABELS, RISK_COLORS } from '../lib/constants';
import { LogoMini } from '../components/Logo';
import AddPatientModal from '../components/AddPatientModal';
import PatientDetail from '../components/PatientDetail';

export default function AdminScreen({ onLogout }) {
  const [patients, setPatients] = useState([]);
  const [patientSessions, setPatientSessions] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [editingPatient, setEditingPatient] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { loadPatients(); }, []);

  const loadPatients = async () => {
    setLoading(true);
    const list = await getAllPatients();
    const sessMap = {}; const alerts = [];
    for (const p of list) {
      const s = await getSessionsForPatient(p.id);
      sessMap[p.id] = s;
      const crisis = s.slice(0, 5).find(x => x.risk === 'crisis');
      if (crisis) alerts.push({ patient: p, session: crisis });
    }
    setPatients(list); setPatientSessions(sessMap); setCriticalAlerts(alerts); setLoading(false);
  };

  const handleAddOrUpdate = async (data) => {
    if (editingPatient) {
      await updatePatient(editingPatient.id, data);
    } else {
      await addPatient({ id: 'p_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8), ...data, is_active: true, created_at: new Date().toISOString() });
    }
    setShowAddModal(false); setEditingPatient(null); loadPatients();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المريضة وجميع جلساتها؟')) return;
    await deletePatient(id);
    if (selectedId === id) setSelectedId(null);
    loadPatients();
  };

  const handleEdit = (patient) => { setEditingPatient(patient); setShowAddModal(true); };

  const timeSince = (d) => {
    const s = Math.floor((Date.now() - new Date(d)) / 1000);
    if (s < 60) return 'منذ ثوانٍ'; if (s < 3600) return `منذ ${Math.floor(s / 60)}د`;
    if (s < 86400) return `منذ ${Math.floor(s / 3600)}س`; return `منذ ${Math.floor(s / 86400)} يوم`;
  };

  const selectedPatient = patients.find(p => p.id === selectedId);
  const selectedSessions = selectedId ? (patientSessions[selectedId] || []) : [];

  const totalPatients = patients.length;
  const totalSessions = Object.values(patientSessions).reduce((a, s) => a + s.length, 0);
  const criticalCount = Object.values(patientSessions).reduce((a, s) => a + s.filter(x => ['alert', 'crisis'].includes(x.risk)).length, 0);

  const filtered = patients.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.diagnosis?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--bg)' }}>
      {/* Ambient */}
      <div className="orb" style={{ top: '5%', right: '-5%', width: '360px', height: '360px', background: '#b8afff', opacity: 0.25 }}></div>
      <div className="orb" style={{ bottom: '20%', left: '-8%', width: '320px', height: '320px', background: '#a4d6cc', opacity: 0.22 }}></div>

      {/* Header */}
      <div className="glass-warm border-b border-[var(--border)] px-6 py-3.5 flex items-center gap-3 sticky top-0 z-30">
        <LogoMini size={44} />
        <div>
          <h1 className="text-[16px] font-extrabold" style={{ color: 'var(--text)' }}>لوحة الإشراف — سلام</h1>
          <p className="text-[11px]" style={{ color: 'var(--text-2)' }}>إدارة المريضات والتقارير الذكية</p>
        </div>
        <div className="mr-auto flex items-center gap-2">
          <button onClick={() => { setEditingPatient(null); setShowAddModal(true); }} className="flex items-center gap-1.5 rounded-2xl px-4 py-2.5 text-white text-[13px] font-extrabold hover:opacity-95 hover:scale-[1.02] transition-all shadow-lg" style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }}>
            <Plus className="w-4 h-4" />
            إضافة مريضة
          </button>
          <button onClick={onLogout} className="bg-white hover:bg-[var(--surface-2)] border border-[var(--border)] rounded-2xl px-3 py-2.5 text-[13px] font-semibold flex items-center gap-1.5 transition-colors" style={{ color: 'var(--text-2)' }}>
            <LogOut className="w-3.5 h-3.5" />
            خروج
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6 max-w-6xl mx-auto relative z-10">
        {/* Critical alerts */}
        {criticalAlerts.length > 0 && (
          <div className="rounded-2xl px-5 py-4 mb-5 animate-fade-in flex items-start gap-3" style={{ background: 'var(--danger-bg)', border: '2px solid rgba(229,133,133,0.4)' }}>
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(229,133,133,0.2)' }}>
              <ShieldAlert className="w-5 h-5" style={{ color: 'var(--danger)' }} />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-extrabold mb-1" style={{ color: '#a35555' }}>تنبيه عاجل — {criticalAlerts.length} حالة</div>
              {criticalAlerts.slice(0, 2).map((a, i) => (
                <div key={i} className="text-[12.5px] leading-relaxed" style={{ color: '#b06b6b' }}>
                  • <span className="font-bold">{a.patient.name}</span>: حالة تستدعي تدخلاً في إحدى الجلسات الأخيرة
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <StatCard icon={Users} label="إجمالي المريضات" value={totalPatients} color="#8b7ff0" bg="var(--primary-bg)" />
          <StatCard icon={TrendingUp} label="إجمالي الجلسات" value={totalSessions} color="#5eb2a3" bg="var(--secondary-bg)" />
          <StatCard icon={AlertTriangle} label="تستدعي انتباه" value={criticalCount} color="#f0b16c" bg="var(--honey-bg)" />
        </div>

        {/* Selected patient detail */}
        {selectedPatient && (
          <PatientDetail
            patient={selectedPatient}
            sessions={selectedSessions}
            onClose={() => setSelectedId(null)}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onRefresh={loadPatients}
          />
        )}

        {/* Search */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-3)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="ابحثي بالاسم أو التشخيص..." className="w-full bg-white border border-[var(--border)] focus:border-[var(--primary)] rounded-2xl px-3 py-2.5 pr-10 text-[13px] outline-none" style={{ color: 'var(--text)' }} />
          </div>
        </div>

        {/* Section title */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-5 rounded-full" style={{ background: 'linear-gradient(180deg, #8b7ff0, #5eb2a3)' }}></div>
          <h2 className="text-[13px] font-extrabold uppercase tracking-wider" style={{ color: 'var(--text-2)' }}>قائمة المريضات</h2>
          <span className="text-[11px] font-bold" style={{ color: 'var(--text-3)' }}>({filtered.length})</span>
        </div>

        {/* Patients grid */}
        {loading ? (
          <div className="text-center py-16" style={{ color: 'var(--text-2)' }}>جارٍ التحميل...</div>
        ) : filtered.length === 0 ? (
          <div className="card-soft text-center py-16 border-dashed">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'var(--primary-bg)' }}>
              <Users className="w-6 h-6" style={{ color: 'var(--primary-dark)' }} />
            </div>
            <p className="text-[14px] font-extrabold mb-1" style={{ color: 'var(--text)' }}>لا توجد مريضات بعد</p>
            <p className="text-[12px]" style={{ color: 'var(--text-2)' }}>اضغطي “إضافة مريضة” للبدء</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map(p => {
              const sess = patientSessions[p.id] || [];
              const recent = sess.slice(0, 5);
              const lastSess = sess[0];
              const latestFlag = lastSess?.risk || 'none';
              const maxRisk = recent.reduce((m, s) => Math.max(m, s.risk_score || 0), 0);
              const rc = RISK_COLORS[latestFlag];
              const isSelected = selectedId === p.id;
              return (
                <div key={p.id} onClick={() => setSelectedId(isSelected ? null : p.id)}
                     className={`card-soft p-4 cursor-pointer transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-[var(--primary)]' : ''}`}>
                  <div className="flex items-center gap-3 mb-2.5">
                    {p.photo ? (
                      <img src={p.photo} alt={p.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white text-lg font-extrabold" style={{ background: 'linear-gradient(135deg, #8b7ff0, #5eb2a3)' }}>
                        {p.name.charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-extrabold truncate" style={{ color: 'var(--text)' }}>{p.name}</div>
                      <div className="text-[11px] truncate" style={{ color: 'var(--text-2)' }}>{p.diagnosis || '—'}</div>
                    </div>
                    <div className="rounded-xl px-2.5 py-1 text-[10.5px] font-extrabold" style={{ background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}>
                      {RISK_LABELS[latestFlag]}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {(p.medications || []).slice(0, 3).map((m, i) => (
                      <span key={i} className="rounded-lg px-2 py-0.5 text-[10.5px] font-semibold" style={{ background: 'var(--secondary-bg)', color: 'var(--secondary-dark)', border: '1px solid var(--secondary-light)' }}>
                        {typeof m === 'string' ? m : m.name}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-[11px]" style={{ color: 'var(--text-2)' }}>
                    <div className="flex items-center gap-1"><Activity className="w-3 h-3" /> {sess.length} جلسة</div>
                    <div className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> <span className="font-bold" style={{ color: maxRisk >= 7 ? 'var(--danger)' : maxRisk >= 4 ? 'var(--warning)' : 'var(--success)' }}>{maxRisk}/10</span></div>
                    <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {lastSess ? timeSince(lastSess.created_at) : '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddPatientModal existing={editingPatient} onClose={() => { setShowAddModal(false); setEditingPatient(null); }} onSave={handleAddOrUpdate} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="card-soft p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: bg }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-2)' }}>{label}</div>
        <div className="text-2xl font-extrabold" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}
